import type { DivisiblePart, DivisibleRemote } from './types';
import { readonly } from 'vue';
import DivisibleTarget from './DivisibleTarget';
import { Observable, Subscription } from 'rxjs';

export class DivisibleReceiver implements DivisibleRemote {
    public static allOrKeysMethod<K extends keyof DivisibleTarget>(
        targetMethod: K
    ) {
        return function (
            this: DivisibleReceiver,
            key?: string,
            ...keys: string[]
        ) {
            const it = !key
                ? this.map.values()
                : [key, ...keys].map((k) => this.map.get(k));
            for (const i of it) {
                if (i) (i as any)[targetMethod]();
            }
            return this;
        };
    }

    private map: Map<string, DivisibleTarget>;

    constructor() {
        this.map = new Map();
    }

    public get(key: string): DivisibleTarget {
        const old = this.map.get(key);
        if (old) return old;

        const res = new DivisibleTarget();
        this.map.set(key, res);
        return res;
    }

    public use(key: string) {
        return this.get(key).use();
    }

    // IMPLEMENT: DynamicRemote

    /**
     * Lists all the target keys that are used.
     */
    public *keys(): Generator<string> {
        for (const [k, v] of this.map) {
            if (v.has()) yield k;
        }
    }

    /**
     * Checks if a target key is used.
     *
     * @param target The target key.
     */
    public has(target: string) {
        return this.map.get(target)?.has() ?? false;
    }

    /**
     * Gives a dynamic part to the target subject.
     *
     * It will do nothing and return an empty subscription if the target isn't used.
     *
     * @param target The target key.
     * @param observable An observable for the dynamic part.
     */
    public give(
        target: string,
        observable: Observable<DivisiblePart>
    ): Subscription {
        return this.map.get(target)?.give(observable) ?? Subscription.EMPTY;
    }

    // PROVIDE THE CONNECTOR

    /**
     * Creates a new remote for this dynamic receiver.
     */
    public remote(): Readonly<DivisibleRemote> {
        return readonly({
            has: (t) => this.has(t),
            keys: () => this.keys(),
            give: (t, o) => this.give(t, o),
        });
    }

    public static emptyRemote(): DivisibleRemote {
        return {
            has: () => false,
            keys: () => [],
            give: () => Subscription.EMPTY,
        };
    }
}

/**
 * Constructor for an dynamic remote which is not connected to any receiver.
 */
export const emptyRemote = () => DivisibleReceiver.emptyRemote();
